import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useConsoleAuth, isMasterUser } from "../../contexts/ConsoleAuthContext";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import NetworkView from "./NetworkView";
import OverviewView from "./OverviewView";
import CoverageView from "./CoverageView";
import ClubsView from "./ClubsView";
import RunsView from "./RunsView";
import SubmissionsView from "./SubmissionsView";
import ExpansionView from "./ExpansionView";
import FeedbackView from "./FeedbackView";
import IntelligenceView from "./IntelligenceView";
import CitiesView from "./CitiesView";
import OnboardingSopView from "./OnboardingSopView";
import FeedbackWidget from "./FeedbackWidget";
import AssistantWidget from "./AssistantWidget";
import ActivationView from "./activation/ActivationView";
import ContentHubView from "./ContentHubView";
import StudioView from "./StudioView";
import UsersView from "./UsersView";
import {
  CONSOLE_RANGE_OPTIONS,
  getCityFromSearchParams,
  getConsoleCityTabPath,
  getConsoleMeetingsRangeFromSearchParams,
  getConsoleRecurringQualityRangeLabel,
  setConsoleMeetingsRangeOnSearchParams,
} from "../../utils/consoleUtils";
import "../../styles/console.css";

const CITY_TABS = [
  { path: "overview", label: "Overview" },
  { path: "onboarding-sop", label: "Onboarding SOP" },
  { path: "coverage", label: "Coverage" },
  { path: "clubs", label: "Clubs" },
  { path: "runs", label: "Runs" },
  { path: "submissions", label: "Submissions" },
  { path: "expansion", label: "Expansion" },
];

export default function ConsoleLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout: mainLogout } = useAuth();
  const { adminLogout } = useConsoleAuth();
  const isMaster = isMasterUser(user);

  const currentPath = location.pathname;
  const citySlug = getCityFromSearchParams(searchParams) ?? null;
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);
  const isNetwork = (currentPath === "/console" || currentPath === "/console/") && !citySlug;

  const { data: networkData } = useConsoleFetch(
    () => (citySlug ? consoleApi.stats.networkOverview() : Promise.resolve(null)),
    [citySlug]
  );
  const { data: coverageData } = useConsoleFetch(
    () =>
      citySlug
        ? consoleApi.stats.coverage(citySlug, { range_key: meetingsRange })
        : Promise.resolve(null),
    [citySlug, meetingsRange]
  );
  const { data: qualityData, error: qualityError, refetch: refetchQuality } = useConsoleFetch(
    () => (citySlug ? consoleApi.stats.qualityChecks(citySlug) : Promise.resolve(null)),
    [citySlug]
  );
  const { data: submissionsCountData } = useConsoleFetch(
    () =>
      citySlug
        ? consoleApi.submissions
            .counts({ city_id: citySlug })
            .then((r) => (typeof r?.review === "number" ? { count: r.review } : {}))
        : Promise.resolve({ count: 0 }),
    [citySlug],
    { refetchOnWindowFocus: false }
  );
  const cities = Array.isArray(networkData?.cities) ? networkData.cities : [];
  const submissionsReviewCount =
    submissionsCountData != null && typeof submissionsCountData.count === "number"
      ? submissionsCountData.count
      : null;
  /** Longer paths first so e.g. onboarding-sop does not match overview. */
  const currentTab = [...CITY_TABS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((t) => currentPath.startsWith(`/console/${t.path}`));
  const isCitiesPage = currentPath.startsWith("/console/cities");
  const isIntelligencePage = currentPath.startsWith("/console/intelligence");
  const isActivationPage = currentPath.startsWith("/console/activation");
  const isContentHubPage = currentPath.startsWith("/console/content-hub");
  const isStudioPage = currentPath.startsWith("/console/studio");
  const isUsersPage = currentPath.startsWith("/console/users");
  const tabPath = currentTab?.path ?? "overview";

  const recurringQualityUtcRange = getConsoleRecurringQualityRangeLabel(qualityData);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    if (isMaster) {
      await mainLogout();
    }
    await adminLogout();
    navigate("/console/login", { replace: true });
  };

  const switchCity = useCallback(
    (slug) => {
      setCityDropdownOpen(false);
      navigate(getConsoleCityTabPath(tabPath, slug, meetingsRange));
    },
    [navigate, tabPath, meetingsRange]
  );

  const handleMeetingsRangeChange = useCallback(
    (e) => {
      const v = e.target.value;
      setSearchParams((prev) => setConsoleMeetingsRangeOnSearchParams(prev, v));
    },
    [setSearchParams]
  );

  const renderView = () => {
    if (isNetwork) return <NetworkView />;
    if (currentPath.startsWith("/console/cities")) return <CitiesView />;
    if (currentPath.startsWith("/console/activation")) return <ActivationView />;
    if (currentPath.startsWith("/console/intelligence")) return <IntelligenceView />;
    if (currentPath.startsWith("/console/feedback")) return <FeedbackView />;
    if (currentPath.startsWith("/console/content-hub")) return <ContentHubView />;
    if (currentPath.startsWith("/console/studio")) return <StudioView />;
    if (currentPath.startsWith("/console/users")) return <UsersView />;
    if (currentPath.startsWith("/console/onboarding-sop")) return <OnboardingSopView />;
    if (currentPath.startsWith("/console/overview"))
      return (
        <OverviewView
          qualityData={qualityData}
          qualityError={qualityError}
          recurringQualityUtcRange={recurringQualityUtcRange}
        />
      );
    if (currentPath.startsWith("/console/coverage")) return <CoverageView />;
    if (currentPath.startsWith("/console/clubs"))
      return (
        <ClubsView
          onQualityMetricsChange={citySlug ? refetchQuality : undefined}
          recurringQualityUtcRange={citySlug ? recurringQualityUtcRange : null}
        />
      );
    if (currentPath.startsWith("/console/runs")) return <RunsView />;
    if (currentPath.startsWith("/console/submissions")) return <SubmissionsView />;
    if (currentPath.startsWith("/console/expansion")) return <ExpansionView />;
    return <NetworkView />;
  };

  const cityTabTo = useCallback(
    (path) => getConsoleCityTabPath(path, citySlug, meetingsRange),
    [citySlug, meetingsRange]
  );

  return (
    <div className="console-page">
      <header className="console-topbar tb-v2">
        <div className="tb-brand console-tb-left">
          <div className="tb-eyebrow console-eyebrow">
            {citySlug ? `RunCards · ${coverageData?.city_name ?? ""}` : "RunCards · Network"}
          </div>
          <h1 className="tb-title console-title">
            <Link to="/console" className="console-title-link">Operations Console</Link>
          </h1>
          <div className="console-sub">
            {citySlug ? `${coverageData?.city_name ?? ""} — city ops` : "City seeding + network monitoring"}
          </div>
        </div>
        {citySlug && (
          <nav className="tb-nav console-tabs" aria-label="City tabs">
            {CITY_TABS.filter((tab) => !tab.hideInHeader).map((tab) => {
              const isActive = currentTab?.path === tab.path;
              return (
                <Link
                  key={tab.path}
                  to={cityTabTo(tab.path)}
                  className={`nav-btn console-tab-btn ${isActive ? "active" : ""}`}
                >
                  {tab.label}
                  {tab.path === "submissions" &&
                    submissionsReviewCount != null &&
                    submissionsReviewCount > 0 && (
                      <span className="console-tab-badge" aria-label={`${submissionsReviewCount} need review`}>
                        {submissionsReviewCount}
                      </span>
                    )}
                </Link>
              );
            })}
          </nav>
        )}
        <div className="tb-right console-tb-right">
          {citySlug ? (
            <div className="console-city-switcher-wrap" ref={dropdownRef}>
              <button
                type="button"
                className="console-city-switcher console-city-switcher-btn tb-btn"
                onClick={() => setCityDropdownOpen((o) => !o)}
                aria-expanded={cityDropdownOpen}
              >
                <span className="console-city-switcher-name">{coverageData?.city_name ?? ""}</span>
                <span className="console-city-switcher-arrow">▾</span>
              </button>
              {cityDropdownOpen && cities.length > 0 && (
                <div className="console-city-dropdown">
                  <Link
                    to="/console"
                    className="console-city-dropdown-item"
                    onClick={() => setCityDropdownOpen(false)}
                  >
                    ← Network Overview
                  </Link>
                  {cities.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`console-city-dropdown-item ${c.id === citySlug ? "active" : ""}`}
                      onClick={() => switchCity(c.id)}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="console-city-switcher">
              <span className="console-city-switcher-name">Network Overview</span>
              <span className="console-city-switcher-arrow">▾</span>
            </span>
          )}
          <Link
            to="/console/intelligence"
            className={`tb-btn ${isIntelligencePage ? "active" : ""}`}
            title="Intelligence"
          >
            Intelligence
          </Link>
          <Link
            to="/console/activation"
            className={`tb-btn ${isActivationPage ? "active" : ""}`}
            title="Activation"
          >
            Activation
          </Link>
          <Link
            to="/console/content-hub"
            className={`tb-btn ${isContentHubPage ? "active" : ""}`}
            title="Marketing Content Hub"
          >
            Content Hub
          </Link>
          <Link
            to="/console/studio"
            className={`tb-btn ${isStudioPage ? "active" : ""}`}
            title="Instagram Studio — compose & post"
          >
            Studio
          </Link>
          <Link
            to="/console/users"
            className={`tb-btn ${isUsersPage ? "active" : ""}`}
            title="Users — runners vs run clubs, signups"
          >
            Users
          </Link>
          <Link
            to="/console/cities"
            className={`tb-btn ${isCitiesPage ? "active" : ""}`}
            title="Cities"
          >
            Cities
          </Link>
          <button type="button" onClick={handleLogout} className="tb-btn">
            Log out
          </button>
        </div>
      </header>

      <main className="console-main">
        {citySlug && (
          <div className="console-ctx-bar">
            <div className="console-ctx-item console-ctx-item--range">
              <span className="console-ctx-label">Schedule window</span>
              <select
                className="console-ctx-range-select"
                value={meetingsRange}
                onChange={handleMeetingsRangeChange}
                aria-label="Schedule window for coverage, overview metrics, runs list, and club upcoming or completed run counts"
              >
                {CONSOLE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">City</span>
              <span className="console-ctx-val">{coverageData?.city_name ?? ""}</span>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">Phase</span>
              <span
                className={`console-ctx-val ${coverageData?.phase ? `phase-${String(coverageData.phase).toLowerCase()}` : ""}`}
              >
                {coverageData?.phase ?? ""}
              </span>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">Coverage</span>
              <span className="console-ctx-val">
                {coverageData?.coverage_score != null ? `${coverageData.coverage_score}%` : ""}
              </span>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">Slots</span>
              <span className="console-ctx-val">
                {coverageData?.health_banner?.covered_slots != null
                  ? `${coverageData.health_banner.covered_slots}/14`
                  : ""}
              </span>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">Fragile Slots</span>
              <span className="console-ctx-val">
                {typeof coverageData?.stability_forecast?.fragile_count === "number"
                  ? coverageData.stability_forecast.fragile_count
                  : ""}
              </span>
            </div>
            <div className="console-ctx-item">
              <span className="console-ctx-label">Next Leverage Move</span>
              <span className="console-ctx-val">{coverageData?.next_leverage_move ?? ""}</span>
            </div>
          </div>
        )}
        {citySlug && qualityData && !qualityError && (
          <div className="next-best-action-rail">
            {(() => {
              // Priority: hard blockers first, then first gap, then soft warnings
              const firstBlocker = Array.isArray(qualityData?.hard_blockers) && qualityData.hard_blockers.length > 0
                ? qualityData.hard_blockers[0]
                : null;
              const firstGap = Array.isArray(coverageData?.gaps) && coverageData.gaps.length > 0
                ? coverageData.gaps[0]
                : null;
              const firstWarning = Array.isArray(qualityData?.soft_warnings) && qualityData.soft_warnings.length > 0
                ? qualityData.soft_warnings[0]
                : null;

              if (firstBlocker) {
                const showRecurringWindow =
                  /recurring/i.test(firstBlocker.title ?? "") &&
                  recurringQualityUtcRange != null &&
                  recurringQualityUtcRange !== "";
                return (
                  <div className="next-action-item next-action-blocker">
                    <span className="next-action-icon">✗</span>
                    <div className="next-action-content">
                      <div className="next-action-title">{firstBlocker.title}</div>
                      <div className="next-action-desc">{firstBlocker.description}</div>
                      {showRecurringWindow && (
                        <div className="next-action-desc" style={{ marginTop: 6, fontSize: 11, opacity: 0.9 }}>
                          Window: {recurringQualityUtcRange}
                        </div>
                      )}
                    </div>
                    <Link
                      to={
                        firstBlocker.title.includes("anchor")
                          ? getConsoleCityTabPath("clubs", citySlug, meetingsRange)
                          : firstBlocker.title.includes("runs")
                          ? getConsoleCityTabPath("runs", citySlug, meetingsRange)
                          : firstBlocker.title.includes("coverage")
                          ? getConsoleCityTabPath("coverage", citySlug, meetingsRange)
                          : getConsoleCityTabPath("overview", citySlug, meetingsRange)
                      }
                      className="next-action-link"
                    >
                      Fix →
                    </Link>
                  </div>
                );
              }
              if (firstGap) {
                return (
                  <div className="next-action-item next-action-gap">
                    <span className="next-action-icon">📍</span>
                    <div className="next-action-content">
                      <div className="next-action-title">
                        Fill {firstGap.day} {firstGap.slot?.toUpperCase()} slot
                      </div>
                      <div className="next-action-desc">Empty or fragile slot needs a recurring run</div>
                    </div>
                    <Link
                      to={getConsoleCityTabPath("runs", citySlug, meetingsRange)}
                      className="next-action-link"
                    >
                      Add Run →
                    </Link>
                  </div>
                );
              }
              if (firstWarning) {
                return (
                  <div className="next-action-item next-action-warning">
                    <span className="next-action-icon">⚠</span>
                    <div className="next-action-content">
                      <div className="next-action-title">{firstWarning.title}</div>
                      <div className="next-action-desc">{firstWarning.description}</div>
                    </div>
                  </div>
                );
              }
              return (
                <div className="next-action-item next-action-complete">
                  <span className="next-action-icon">✓</span>
                  <div className="next-action-content">
                    <div className="next-action-title">Onboarding complete</div>
                    <div className="next-action-desc">All SOP requirements met</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {renderView()}
      </main>
      <FeedbackWidget />
      <AssistantWidget />
    </div>
  );
}
